import {
  CHECKLIST_SECTION_LABELS,
  checklistPersonSchema,
  eventChecklistSchema,
  type AdminEvent,
  type ChecklistItem,
  type ChecklistPerson,
  type ChecklistSection,
  type EventChecklist,
} from '@growup/site-shared';
import {
  Autocomplete,
  Button,
  Card,
  cx,
  DateInput,
  Field,
  Input,
  Notice,
  Select,
  Textarea,
} from '@growup/ui';
import { useCallback, useEffect, useState, type DragEvent, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';

const SECTIONS: ChecklistSection[] = ['prep', 'wrap_up'];

type Filter = 'all' | 'mine' | 'overdue';
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'mine', label: 'Os meus' },
  { id: 'overdue', label: 'Prazo passado' },
];

const formatDay = (day: string) => day.split('-').reverse().slice(0, 2).join('/');

function summaryLine(s: EventChecklist['summary']): string {
  const days =
    s.daysToEvent > 1
      ? `faltam ${String(s.daysToEvent)} dias para o evento`
      : s.daysToEvent === 1
        ? 'o evento é amanhã'
        : s.daysToEvent === 0
          ? 'o evento é hoje'
          : 'o evento já começou';
  return [
    `${String(s.total)} ${s.total === 1 ? 'item' : 'itens'}`,
    `${String(s.done)} ${s.done === 1 ? 'feito' : 'feitos'}`,
    ...(s.overdue > 0 ? [`${String(s.overdue)} com o prazo passado`] : []),
    days,
  ].join(' · ');
}

/**
 * Checklist do evento (docs/eventos, RF-E15 a RF-E18): as tarefas de
 * preparacao e de fecho, sem zona nem hora. O que acontece durante o evento
 * fica nos Turnos. Precisa de `events.manage`; a ligacao a um custo so aparece
 * a quem tambem tem `events.budget`.
 */
export function ChecklistView({
  event,
  events,
  me,
}: {
  event: AdminEvent;
  /** Os outros eventos, para copiar a checklist de um deles. */
  events: AdminEvent[];
  /** A conta de quem esta a ver, para o filtro «Os meus». */
  me: string;
}) {
  const base = `/api/site/events/${event.id}/checklist`;
  const [list, setList] = useState<EventChecklist | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<{ id: string | null; section: ChecklistSection } | null>(
    null,
  );
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson(base, eventChecklistSchema)
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [base]);
  useEffect(load, [load]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      setList(eventChecklistSchema.parse(await action()));
    } catch (e) {
      setError(messageOf(e));
      load();
    } finally {
      setBusy(false);
    }
  };

  if (list === null) {
    return error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>;
  }

  const shown = (i: ChecklistItem) =>
    filter === 'all' || (filter === 'mine' ? i.assignee?.accountId === me : i.overdue);
  // So se muda a ordem com a lista inteira a vista.
  const canMove = filter === 'all' && !busy;

  const move = (section: ChecklistSection, id: string, to: number) => {
    const ids = list.items.filter((i) => i.section === section).map((i) => i.id);
    const from = ids.indexOf(id);
    if (from < 0 || to < 0 || to >= ids.length || from === to) return;
    ids.splice(to, 0, ...ids.splice(from, 1));
    // Mostra ja a ordem nova; a resposta da API confirma.
    setList({
      ...list,
      items: [
        ...list.items.filter((i) => i.section !== section || !ids.includes(i.id)),
        ...ids.map((x) => list.items.find((i) => i.id === x) as ChecklistItem),
      ].sort((a, b) => SECTIONS.indexOf(a.section) - SECTIONS.indexOf(b.section)),
    });
    void run(() => send('PUT', `${base}/order`, { section, ids }));
  };

  if (editing !== null) {
    return (
      <ItemEditor
        key={editing.id ?? `new-${editing.section}`}
        base={base}
        item={list.items.find((i) => i.id === editing.id) ?? null}
        section={editing.section}
        costs={list.costs}
        onSaved={(l) => {
          setList(l);
          setEditing(null);
        }}
        onClose={() => setEditing(null)}
      />
    );
  }

  const others = events.filter((e) => e.id !== event.id);

  return (
    <Card
      headingLevel={3}
      title="Checklist"
      actions={
        <div className="flex flex-wrap gap-2">
          {others.length > 0 ? (
            <Button size="sm" variant="secondary" onClick={() => setCopying((c) => !c)}>
              Copiar de outro evento
            </Button>
          ) : null}
          <Button size="sm" onClick={() => setEditing({ id: null, section: 'prep' })}>
            Novo item
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">{summaryLine(list.summary)}</p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {copying ? (
          <CopyFrom
            events={others}
            busy={busy}
            onCopy={(fromEventId) =>
              void run(async () => {
                const res = await send('POST', `${base}/copy`, { fromEventId });
                setCopying(false);
                return res;
              })
            }
          />
        ) : null}
        <div role="group" aria-label="Filtro" className="flex w-fit border border-gu-line">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={filter === f.id}
              onClick={() => setFilter(f.id)}
              className={cx(
                'px-3 py-2 font-sub text-label font-bold uppercase',
                filter === f.id
                  ? 'bg-gu-accent text-gu-on-accent'
                  : 'text-gu-ink-muted hover:text-gu-ink',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {SECTIONS.map((section) => {
          const all = list.items.filter((i) => i.section === section);
          const items = all.filter(shown);
          return (
            <section key={section} className="flex flex-col gap-1">
              <h4 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
                {CHECKLIST_SECTION_LABELS[section]}
              </h4>
              {items.length === 0 ? (
                <p className="py-2 text-gu-ink-muted">
                  {all.length === 0 ? 'Ainda não há itens.' : 'Nenhum item neste filtro.'}
                </p>
              ) : (
                <ul aria-label={CHECKLIST_SECTION_LABELS[section]}>
                  {items.map((item) => {
                    const index = all.indexOf(item);
                    const onDrop = (e: DragEvent) => {
                      e.preventDefault();
                      if (dragging) move(section, dragging, index);
                      setDragging(null);
                    };
                    return (
                      <li
                        key={item.id}
                        draggable={canMove}
                        onDragStart={() => setDragging(item.id)}
                        onDragEnd={() => setDragging(null)}
                        onDragOver={(e) => {
                          if (canMove && all.some((i) => i.id === dragging)) e.preventDefault();
                        }}
                        onDrop={onDrop}
                        className={cx(
                          'flex items-center gap-3 border-b border-gu-line py-2',
                          dragging === item.id && 'opacity-50',
                        )}
                      >
                        {canMove ? (
                          <span className="flex flex-col">
                            <button
                              type="button"
                              className="px-1 text-caption text-gu-ink-muted hover:text-gu-ink disabled:opacity-30"
                              aria-label={`Subir «${item.title}»`}
                              disabled={index === 0}
                              onClick={() => move(section, item.id, index - 1)}
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              className="px-1 text-caption text-gu-ink-muted hover:text-gu-ink disabled:opacity-30"
                              aria-label={`Descer «${item.title}»`}
                              disabled={index === all.length - 1}
                              onClick={() => move(section, item.id, index + 1)}
                            >
                              ▼
                            </button>
                          </span>
                        ) : null}
                        <input
                          type="checkbox"
                          className="size-5 shrink-0 cursor-pointer accent-[rgb(var(--gu-accent))]"
                          checked={item.doneAt !== null}
                          disabled={busy}
                          aria-label={`Feito: ${item.title}`}
                          onChange={(e) => {
                            const done = e.target.checked;
                            void run(() => send('POST', `${base}/${item.id}/done`, { done }));
                          }}
                        />
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 flex-col text-left"
                          onClick={() => setEditing({ id: item.id, section })}
                        >
                          <span
                            className={cx(
                              'truncate',
                              item.doneAt !== null && 'text-gu-ink-muted line-through',
                            )}
                          >
                            {item.title}
                          </span>
                          {item.financeEntry || item.notes || item.doneBy ? (
                            <span className="truncate text-caption text-gu-ink-muted">
                              {item.doneBy
                                ? `Feito por ${item.doneBy}`
                                : item.financeEntry
                                  ? `Ligado ao custo «${item.financeEntry.description}»`
                                  : item.notes}
                            </span>
                          ) : null}
                        </button>
                        <span className="shrink-0 text-caption text-gu-ink-muted">
                          {item.assignee?.login ?? ''}
                        </span>
                        <span
                          className={cx(
                            'w-12 shrink-0 text-right tabular-nums',
                            item.overdue ? 'font-semibold text-gu-danger' : 'text-gu-ink-muted',
                          )}
                        >
                          {item.dueOn ? formatDay(item.dueOn) : '—'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
        <p className="text-caption text-gu-ink-muted">
          Sem zona nem hora: o que acontece durante o evento fica nos Turnos. Os voluntários veem os
          seus itens em growup.gg/conta/eventos e podem marcá-los como feitos.
        </p>
      </div>
    </Card>
  );
}

function CopyFrom({
  events,
  busy,
  onCopy,
}: {
  events: AdminEvent[];
  busy: boolean;
  onCopy: (fromEventId: string) => void;
}) {
  const [from, setFrom] = useState(events[0]?.id ?? '');
  return (
    <form
      className="flex flex-wrap items-end gap-3 border-2 border-gu-line p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (from) onCopy(from);
      }}
    >
      <Field
        label="Copiar a checklist de"
        hint="Junta os itens no fim, por fazer e sem responsáveis. Os prazos ficam à mesma distância do início do evento."
        className="min-w-64 flex-1"
      >
        <Select value={from} onChange={(e) => setFrom(e.target.value)}>
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {e.title}
            </option>
          ))}
        </Select>
      </Field>
      <Button type="submit" disabled={busy || !from}>
        Copiar
      </Button>
    </form>
  );
}

function ItemEditor({
  base,
  item,
  section: initialSection,
  costs,
  onSaved,
  onClose,
}: {
  base: string;
  item: ChecklistItem | null;
  section: ChecklistSection;
  /** Vazio sem `events.budget`: entao nao se mostra a ligacao. */
  costs: EventChecklist['costs'];
  onSaved: (list: EventChecklist) => void;
  onClose: () => void;
}) {
  const [section, setSection] = useState<ChecklistSection>(item?.section ?? initialSection);
  const [title, setTitle] = useState(item?.title ?? '');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [dueOn, setDueOn] = useState(item?.dueOn ?? '');
  const [assignee, setAssignee] = useState<ChecklistPerson | null>(item?.assignee ?? null);
  const [query, setQuery] = useState('');
  const [financeEntryId, setFinanceEntryId] = useState(item?.financeEntry?.id ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const search = (q: string) =>
    getJson(`${base}/people?q=${encodeURIComponent(q)}`, z.array(checklistPersonSchema));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (title.trim() === '') {
      setFields({ title: 'Escreva o que há a fazer.' });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const body = {
        section,
        title,
        notes,
        dueOn: dueOn || null,
        assigneeAccountId: assignee?.accountId ?? null,
        financeEntryId: financeEntryId || null,
      };
      const res = item
        ? await send('PUT', `${base}/${item.id}`, body)
        : await send('POST', base, body);
      onSaved(eventChecklistSchema.parse(res));
    } catch (err) {
      if (err instanceof ApiError) setFields(err.fields);
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item || !window.confirm(`Apagar «${item.title}»?`)) return;
    setBusy(true);
    try {
      onSaved(eventChecklistSchema.parse(await send('DELETE', `${base}/${item.id}`)));
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={3} title={item ? 'Editar item' : 'Novo item'}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
          <Field label="O que há a fazer" error={fields.title}>
            <Input value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Secção">
            <Select
              value={section}
              onChange={(e) => setSection(e.target.value as ChecklistSection)}
            >
              {SECTIONS.map((s) => (
                <option key={s} value={s}>
                  {CHECKLIST_SECTION_LABELS[s]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Responsável"
            optional
            hint={
              assignee
                ? `${assignee.login}${assignee.volunteer ? ' (voluntário)' : ''}`
                : 'Staff ou um voluntário confirmado neste evento.'
            }
            error={fields.assigneeAccountId}
          >
            <Autocomplete
              value={query}
              onChange={setQuery}
              search={search}
              getKey={(p) => p.accountId}
              renderItem={(p) => (
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-semibold">{p.login}</span>
                  {p.volunteer ? (
                    <span className="text-caption text-gu-ink-muted">voluntário confirmado</span>
                  ) : null}
                </span>
              )}
              onSelect={(p) => {
                setQuery('');
                setAssignee(p);
              }}
              placeholder={assignee ? 'Trocar…' : 'Procurar pelo login…'}
            />
          </Field>
          <Field label="Prazo" optional error={fields.dueOn}>
            <DateInput value={dueOn} onChange={setDueOn} />
          </Field>
        </div>
        {assignee ? (
          <div>
            <Button size="sm" variant="ghost" onClick={() => setAssignee(null)}>
              Tirar o responsável
            </Button>
          </div>
        ) : null}
        <Field label="Notas" optional error={fields.notes}>
          <Textarea
            value={notes}
            maxLength={2000}
            rows={3}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        {costs.length > 0 ? (
          <Field label="Ligado ao custo" optional error={fields.financeEntryId}>
            <Select value={financeEntryId} onChange={(e) => setFinanceEntryId(e.target.value)}>
              <option value="">Nenhum</option>
              {costs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.description}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={busy}>
            Guardar
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          {item ? (
            <Button variant="ghost" onClick={() => void remove()} disabled={busy}>
              Apagar
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
